/**
 * VirtualWallpaperGrid — windowed grid for the wallpaper explore / see-all views.
 *
 * Uses @tanstack/react-virtual to only render rows that are visible (+ overscan).
 * Each row contains `columns` items side-by-side. The card aspect-ratio is 9/14,
 * so each row height = (containerWidth / columns) * (14/9).
 *
 * Props mirror GalleryGrid so they're drop-in compatible.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback, memo } from 'react';
import { useVirtualizer, useWindowVirtualizer } from '@tanstack/react-virtual';
import { motion } from 'framer-motion';
import { ImageIcon } from 'lucide-react';
import { WallpaperCard } from '@/components/WallpaperCard';
import type { WallpapersResponse } from '@/lib/collections/wallpapers';

// ── Memoized card wrapper ─────────────────────────────────────────────────────
// Prevents WallpaperCard re-renders when the parent VirtualWallpaperGrid
// re-renders due to virtualizer scroll updates. The callbacks are stable
// (same reference) across rows because they only change when the parent
// onSelectWallpaper / onToggleFavorite props change.
interface VirtualCardProps {
  wallpaper: WallpapersResponse;
  isFavorited: boolean;
  isPurchased: boolean;
  onSelect: (w: WallpapersResponse, fromLibrary: boolean) => void;
  onToggleFavorite: (w: WallpapersResponse) => void;
  fromLibrary: boolean;
  index: number;
  gridColumns: number;
  minimal: boolean;
}

const VirtualCard = memo(function VirtualCard({
  wallpaper,
  isFavorited,
  isPurchased,
  onSelect,
  onToggleFavorite,
  fromLibrary,
  index,
  gridColumns,
  minimal,
}: VirtualCardProps) {
  // These callbacks are stable closures that close over `wallpaper` once.
  // Because VirtualCard is memoized, they're recreated only when `wallpaper`
  // reference changes (i.e. when actual data changes), not on scroll.
  const handleClick = useCallback(() => onSelect(wallpaper, fromLibrary), [onSelect, wallpaper, fromLibrary]);
  const handleToggleFavorite = useCallback(() => onToggleFavorite(wallpaper), [onToggleFavorite, wallpaper]);

  return (
    <WallpaperCard
      wallpaper={wallpaper}
      isFavorited={isFavorited}
      isPurchased={isPurchased}
      onClick={handleClick}
      onToggleFavorite={handleToggleFavorite}
      index={index}
      gridColumns={gridColumns}
      minimal={minimal}
    />
  );
});

VirtualCard.displayName = 'VirtualCard';

const CARD_ASPECT = 14 / 9; // height / width (taller than wide)
const ROW_GAP = 8; // px between rows
const COL_GAP = 8; // px between columns
const PADDING_H = 8; // horizontal padding on each side
const BOTTOM_PADDING = 110; // clearance for the bottom nav

interface VirtualWallpaperGridProps {
  wallpapers: WallpapersResponse[];
  favoritedIds: Set<string>;
  purchasedIds: Set<string>;
  onToggleFavorite: (w: WallpapersResponse) => void;
  onSelectWallpaper: (w: WallpapersResponse, fromLibrary: boolean) => void;
  gridColumns?: number;
  fromLibrary?: boolean;
  accentColor?: string;
  minimal?: boolean;
  emptyMessage?: string;
  emptyAction?: { label: string; onClick: () => void };
  /** Scroll container element — pass the element whose scrollTop drives virtualization */
  scrollContainer?: HTMLElement | null;
  /** Disable virtualization and render all items directly — use for small lists in fixed overlays */
  disableVirtualization?: boolean;
}

const VirtualWallpaperGrid: React.FC<VirtualWallpaperGridProps> = React.memo(({
  wallpapers,
  favoritedIds,
  purchasedIds,
  onToggleFavorite,
  onSelectWallpaper,
  gridColumns = 2,
  fromLibrary = false,
  accentColor = '#8b5cf6',
  minimal = false,
  emptyMessage = 'No wallpapers yet',
  emptyAction,
  scrollContainer,
  disableVirtualization = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Measure container width
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? el.offsetWidth;
      setContainerWidth(w);
    });
    ro.observe(el);
    setContainerWidth(el.offsetWidth);
    return () => ro.disconnect();
  }, []);

  const cols = Math.max(1, gridColumns);

  // Compute row height: (col_width) * aspect_ratio
  const colWidth = containerWidth > 0
    ? (containerWidth - PADDING_H * 2 - COL_GAP * (cols - 1)) / cols
    : 160;
  const rowHeight = Math.round(colWidth * CARD_ASPECT);

  // Chunk wallpapers into rows
  const rows = useMemo(() => {
    const result: WallpapersResponse[][] = [];
    for (let i = 0; i < wallpapers.length; i += cols) {
      result.push(wallpapers.slice(i, i + cols));
    }
    return result;
  }, [wallpapers, cols]);

  // When a scrollContainer is provided (e.g. the SeeAll modal), use the
  // element-based virtualizer. When scrollContainer is absent the page
  // scrolls via the window, so we use useWindowVirtualizer instead —
  // returning null from getScrollElement causes @tanstack/virtual-core to
  // exit early in _willUpdate and never render any items.

  // scrollMargin: distance from the top of the document to the top of our
  // outer container — required by useWindowVirtualizer so it can map window
  // scrollY to item indices correctly.
  const [scrollMargin, setScrollMargin] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || scrollContainer) return; // only needed for window mode
    let rafId: number | null = null;
    const updateMargin = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => setScrollMargin(el.offsetTop));
    };
    updateMargin();
    // Re-measure when the layout shifts (e.g. tab switch reveals the section).
    // Deferred via rAF above so a synchronous doc-height jump (e.g. an inactive
    // section collapsing/expanding) doesn't force this recompute inside the
    // same frame as the section-switch animation.
    const ro = new ResizeObserver(updateMargin);
    ro.observe(document.documentElement);
    return () => {
      ro.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [scrollContainer]);

  // Element-based virtualizer (modal / custom scroll container)
  const containerVirtualizer = useVirtualizer({
    count: scrollContainer ? rows.length : 0,
    getScrollElement: useCallback(() => scrollContainer ?? null, [scrollContainer]),
    estimateSize: () => rowHeight + ROW_GAP,
    overscan: 4,
  });

  // Window-based virtualizer (default explore page)
  const windowVirtualizer = useWindowVirtualizer({
    count: scrollContainer ? 0 : rows.length,
    estimateSize: () => rowHeight + ROW_GAP,
    overscan: 4,
    scrollMargin,
  });

  const virtualizer = scrollContainer ? containerVirtualizer : windowVirtualizer;

  // Recompute when rowHeight actually changes. Guarded against the identity
  // of `virtualizer` changing on unrelated re-renders, and deferred to the
  // next animation frame so the (potentially expensive, full-catalog)
  // recompute never runs synchronously inside the frame that starts a
  // section-switch animation.
  const prevRowHeightRef = useRef(rowHeight);
  useEffect(() => {
    if (prevRowHeightRef.current === rowHeight) return;
    prevRowHeightRef.current = rowHeight;
    const id = requestAnimationFrame(() => virtualizer.measure());
    return () => cancelAnimationFrame(id);
  }, [rowHeight, virtualizer]);

  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(accentColor);
  const rgb = r ? `${parseInt(r[1], 16)},${parseInt(r[2], 16)},${parseInt(r[3], 16)}` : '139,92,246';

  if (wallpapers.length === 0) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '80px 24px', gap: 16, textAlign: 'center',
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: `rgba(${rgb},0.12)`,
          border: `1px solid rgba(${rgb},0.25)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ImageIcon size={28} color={accentColor} />
        </div>
        <p style={{ color: 'rgba(240,240,245,0.45)', fontSize: 15, margin: 0, maxWidth: 280 }}>
          {emptyMessage}
        </p>
        {emptyAction && (
          <motion.button
            whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
            onClick={emptyAction.onClick}
            style={{
              padding: '10px 22px', borderRadius: 10,
              background: `rgba(${rgb},0.8)`,
              border: 'none', color: '#fff',
              cursor: 'pointer', fontSize: 14, fontWeight: 700,
              fontFamily: "'Syne', sans-serif",
            }}
          >
            {emptyAction.label}
          </motion.button>
        )}
      </div>
    );
  }

  // Non-virtualized path: render all rows directly with natural height.
  // Used for small lists inside fixed overlays (e.g. search results panel)
  // where the window virtualizer would produce an oversized spacer.
  if (disableVirtualization) {
    return (
      <div style={{ padding: `0 ${PADDING_H}px`, display: 'flex', flexDirection: 'column', gap: ROW_GAP }}>
        {rows.map((rowItems, rowIndex) => (
          <div
            key={rowIndex}
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gap: COL_GAP,
            }}
          >
            {rowItems.map((w, colIdx) => {
              const absoluteIndex = rowIndex * cols + colIdx;
              return (
                <VirtualCard
                  key={w.id}
                  wallpaper={w}
                  isFavorited={favoritedIds.has(w.id)}
                  isPurchased={purchasedIds.has(w.id)}
                  onSelect={onSelectWallpaper}
                  onToggleFavorite={onToggleFavorite}
                  fromLibrary={fromLibrary}
                  index={absoluteIndex}
                  gridColumns={gridColumns}
                  minimal={minimal}
                />
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  const totalHeight = virtualizer.getTotalSize() + BOTTOM_PADDING;
  // For window virtualizer, each item's `start` is relative to the document
  // origin. We need to subtract scrollMargin so items are positioned relative
  // to our container instead of the document top.
  const itemOffset = scrollContainer ? 0 : scrollMargin;

  return (
    <div
      ref={containerRef}
      style={{ padding: `0 ${PADDING_H}px` }}
    >
      {/* Outer div sized to total virtual height so the scrollbar is correct */}
      <div style={{ height: totalHeight, position: 'relative' }}>
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const rowItems = rows[virtualRow.index];
          if (!rowItems) return null;
          return (
            <div
              key={virtualRow.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                transform: `translateY(${virtualRow.start - itemOffset}px)`,
                display: 'grid',
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gap: COL_GAP,
                willChange: 'transform',
              }}
            >
              {rowItems.map((w, colIdx) => {
                const absoluteIndex = virtualRow.index * cols + colIdx;
                return (
                  <VirtualCard
                    key={w.id}
                    wallpaper={w}
                    isFavorited={favoritedIds.has(w.id)}
                    isPurchased={purchasedIds.has(w.id)}
                    onSelect={onSelectWallpaper}
                    onToggleFavorite={onToggleFavorite}
                    fromLibrary={fromLibrary}
                    index={absoluteIndex}
                    gridColumns={gridColumns}
                    minimal={minimal}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
});

VirtualWallpaperGrid.displayName = 'VirtualWallpaperGrid';

export default VirtualWallpaperGrid;
